/**
 * BacktestPanel — focal renderer for "run a backtest".
 * Wraps /api/v1/strategies/compare for v0.3 (single SIM run = champion vs
 * challenger over N days). When the gate is promotable, exposes a Promote
 * button that calls /strategies/promote/{candidate}.
 */
import { useState } from 'react';
import * as api from '../lib/api';

export function BacktestPanel() {
  const [days, setDays] = useState(30);
  const [capital, setCapital] = useState(100_000);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.strategies.compare>> | null>(null);
  const [promoteOut, setPromoteOut] = useState<{ promoted: boolean; pr_url: string | null; reason: string | null } | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    setData(null);
    setPromoteOut(null);
    try {
      setData(await api.strategies.compare(days, capital));
    } catch (e: unknown) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function promote() {
    setBusy(true);
    try {
      setPromoteOut(await api.strategies.promote('challenger', days));
    } catch (e: unknown) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="card p-3 grid grid-cols-3 gap-2 items-end">
        <label className="block">
          <span className="text-[11px] text-ink-mute">days</span>
          <input
            type="number"
            className="input w-full mt-1"
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value)))}
          />
        </label>
        <label className="block">
          <span className="text-[11px] text-ink-mute">capital ($)</span>
          <input
            type="number"
            className="input w-full mt-1"
            value={capital}
            onChange={(e) => setCapital(Math.max(0, Number(e.target.value)))}
          />
        </label>
        <button className="btn-primary" onClick={run} disabled={busy}>
          {busy ? 'running…' : 'run backtest'}
        </button>
      </div>

      {err && <div className="text-bad text-xs">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <CurveCard
              title={`${data.champion.name} (champion)`}
              curve={data.champion.equity_curve}
              metrics={data.champion.metrics}
            />
            <CurveCard
              title={`${data.challenger.name} (challenger)`}
              curve={data.challenger.equity_curve}
              metrics={data.challenger.metrics}
            />
          </div>
          <div className="card p-3 flex items-center justify-between">
            <div className="text-xs">
              gate:{' '}
              {data.promotion_gate.promotable ? (
                <span className="text-ok">promotable</span>
              ) : (
                <span className="text-warn">
                  not promotable — {(data.promotion_gate.violations ?? []).join(', ')}
                </span>
              )}
            </div>
            <button
              className="btn"
              onClick={promote}
              disabled={busy || !data.promotion_gate.promotable}
            >
              promote challenger →
            </button>
          </div>
          {promoteOut && (
            <div className="card p-3 text-xs space-y-1">
              <div>
                promoted:{' '}
                <span className={promoteOut.promoted ? 'text-ok' : 'text-warn'}>
                  {String(promoteOut.promoted)}
                </span>
              </div>
              {promoteOut.pr_url && (
                <div>
                  PR:{' '}
                  <a href={promoteOut.pr_url} target="_blank" className="text-accent underline">
                    {promoteOut.pr_url}
                  </a>
                </div>
              )}
              {promoteOut.reason && <div className="text-ink-mute">{promoteOut.reason}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CurveCard({
  title,
  curve,
  metrics,
}: {
  title: string;
  curve: number[];
  metrics: Record<string, unknown>;
}) {
  const min = Math.min(...curve);
  const max = Math.max(...curve);
  const range = Math.max(1e-6, max - min);
  const w = 320;
  const h = 100;
  const path = curve
    .map((c, i) => {
      const x = (i / Math.max(1, curve.length - 1)) * w;
      const y = h - ((c - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const totalRet = Number(metrics.total_return_pct ?? 0);
  const sharpe = metrics.sharpe;
  const dd = metrics.max_drawdown_pct;
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold">{title}</div>
        <div
          className={`text-xs num ${
            totalRet >= 0 ? 'text-ok' : 'text-bad'
          }`}
        >
          {totalRet >= 0 ? '+' : ''}
          {totalRet.toFixed(2)}%
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24 block">
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="text-accent"
        />
      </svg>
      <div className="text-[11px] num text-ink-mute mt-1 flex justify-between">
        <span>sharpe {sharpe ? Number(sharpe).toFixed(2) : '—'}</span>
        <span>max DD {dd ? Number(dd).toFixed(2) : '—'}%</span>
        <span>
          {curve[0]?.toFixed(0)} → {curve[curve.length - 1]?.toFixed(0)}
        </span>
      </div>
    </div>
  );
}
